<style>
.color-style-system tr>td:nth-child(1) {
  text-align: left;
}
.color-style-system tr>td {
  text-align: center;
}
.color-style-system tr>td>div.d-flex {
  margin: 0 auto;
}
</style>

<template>
  <b-row>
    <b-col cols="12">
      <!-- Default Colors -->
      <b-card
        title="Color Palette"
        class="background-color"
      >
        <b-card-text class="mb-0">
          <p>Our color palette is a selection of colors that work together to create consistency in the way we use colors in products. For each color, it has its functionality in the application as alerts of errors <b>(danger)</b>, warnings to the user <b>(warning)</b>, communications of approval <b>(success)</b>, or main color of the application <b>(primary)</b>.</p>
        </b-card-text>
        <div class="d-flex justify-content-start flex-wrap">
          <div class="text-center bg-primary colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 shadow">
            <span>Primary</span>
          </div>
          <div class="text-center bg-secondary colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow">
            <span>Secondary</span>
          </div>
          <div class="text-center bg-success colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow">
            <span>Success</span>
          </div>
          <div class="text-center bg-warning colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow">
            <span>Alert</span>
          </div>
          <div class="text-center bg-danger colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow">
            <span>Error</span>
          </div>
          <div class="text-center bg-info colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center my-1 ml-50 shadow">
            <span>Info</span>
          </div>
        </div>
      </b-card>
    </b-col>
    <b-col cols="12">
      <!-- Default Colors -->
      <b-card
        title="Color Style System"
        class="background-color"
      >
        <b-card-text class="mb-0">
          <p>To create the style system, we create a table where each column represents a state. We define a common set of states such as Default, Disable, Highlight, Error, Warning, Success, etc. We can easily create this by looking at different states of components in our existing design system. Then, we put different components in each row and add the style of different states in most relevant columns.</p>

          <p>To represent a style, we resemble the style with squares (and concentric squares for frame and content). We apply all the styles from our original component to the squares such as background, border, corner radius, shadow, etc.</p>

          <p>
            <span
              style="cursor: pointer;"
              @click="skin = isDark ? 'light' : 'dark'"
            >
              Select Color Theme:
              <feather-icon
                size="21"
                :icon="`${isDark ? 'Sun' : 'Moon'}Icon`"
              />
            </span>
          </p>
        </b-card-text>

        <div class="table-responsive-sm color-style-system">
          <table class="table b-table">
            <thead>
              <tr>
                <th />
                <th class="width-150 text-center">
                  <div>Highlight 2</div>
                </th>
                <th class="width-150 text-center">
                  <div>Highlight</div>
                </th>
                <th class="width-150 text-center">
                  <div>Default</div>
                </th>
                <th class="width-150 text-center">
                  <div>Secondary</div>
                </th>
                <th class="width-150 text-center">
                  <div>Disabled</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Background</strong>
                </td>
                <td />
                <td />
                <td>
                  <div class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center border">
                    <span>A</span>
                  </div>
                  <small class="text-muted">Main</small>
                </td>
                <td>
                  <div
                    :class="isDark ? 'bg-darken' : 'bg-light'"
                    class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Secondary</small>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Text</strong>
                </td>
                <td />
                <td>
                  <div
                    class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #5e5873;"
                  />
                  <small class="text-muted">Heading</small>
                </td>
                <td>
                  <div
                    class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #6e6b7b;"
                  />
                  <small class="text-muted">Body</small>
                </td>
                <td>
                  <div
                    class="text-center colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #b9b9c3;"
                  />
                  <small class="text-muted">Muted</small>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Button (Filled)</strong>
                </td>
                <td>
                  <div
                    class="text-center bg-primary bg-darken-2 border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 8px 25px -8px #f9278e;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Pressed</small>
                </td>
                <td>
                  <div
                    class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 8px 25px -8px #f9278e;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Hover</small>
                </td>
                <td>
                  <div class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center">
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                </td>
                <td />
                <td aria-colindex="5">
                  <div
                    class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="opacity: 0.6;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Button (Border)</strong>
                </td>
                <td>
                  <div
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: rgba(249, 39, 142, 0.2)"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Pressed</small>
                </td>
                <td>
                  <div
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: rgba(249, 39, 142, 0.04)"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Hover</small>
                </td>
                <td>
                  <div
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Default</small>
                </td>
                <td />
                <td aria-colindex="5">
                  <div
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="opacity: 0.6;"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Input Field</strong>
                </td>
                <td>
                  <div
                    class="text-center border-primary colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Focused</small>
                </td>
                <td>
                  <div class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center">
                    <span>A</span>
                  </div>
                  <small class="text-muted">Filled</small>
                </td>
                <td>
                  <div
                    :class="isDark ? 'dark-input-placeholder' : 'light-input-placeholder'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Empty</small>
                </td>
                <td>
                  <div
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Read Only</small>
                </td>
                <td>
                  <div
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Select Field</strong>
                </td>
                <td>
                  <div
                    class="text-center border-primary colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Opened</small>
                </td>
                <td>
                  <div class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center">
                    <span>A</span>
                  </div>
                  <small class="text-muted">Selected</small>
                </td>
                <td>
                  <div
                    :class="isDark ? 'dark-input-placeholder' : 'light-input-placeholder'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Empty</small>
                </td>
                <td>
                  <div
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Read Only</small>
                </td>
                <td>
                  <div
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Error</strong>
                </td>
                <td>
                  <div
                    class="text-center border-danger colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Focused</small>
                </td>
                <td>
                  <div class="text-center border-danger colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center">
                    <span>A</span>
                  </div>
                  <small class="text-muted">Filled</small>
                </td>
                <td>
                  <div
                    class="text-center bg-danger text-white colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="color: #b9b9c3;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                </td>
                <td>
                  <div
                    class="text-center alert-danger colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #f2f4f8;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Callout</small>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Success</strong>
                </td>
                <td>
                  <div
                    class="text-center border-success colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Focused</small>
                </td>
                <td>
                  <div class="text-center border-success colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center">
                    <span>A</span>
                  </div>
                  <small class="text-muted">Filled</small>
                </td>
                <td>
                  <div
                    class="text-center bg-success text-white colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="color: #b9b9c3;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                </td>
                <td>
                  <div
                    class="text-center alert-success colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #f2f4f8;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Callout</small>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Alert</strong>
                </td>
                <td />
                <td />
                <td>
                  <div
                    class="text-center bg-warning text-white colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="color: #b9b9c3;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                </td>
                <td>
                  <div
                    class="text-center alert-warning colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #f2f4f8;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Callout</small>
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>
    </b-col>
    <b-col cols="12">
      <!-- Default Colors -->
      <b-card
        title="Tints & Shades Colors"
        class="background-color"
      >
        <b-card-text>
          Use <code>.bg-{color}.bg-lighten-{1 | 2 | 3 | 4 | 5}</code> classes for lighten background color and <code>.bg-{color}.bg-darken-{1 | 2 | 3 | 4}</code> classes for darken background color.
        </b-card-text>
        <div class="d-flex justify-content-start flex-wrap">
          <div class="text-center bg-primary bg-darken-4 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>D-4</span>
          </div>
          <div class="text-center bg-primary bg-darken-3 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>D-3</span>
          </div>
          <div class="text-center bg-primary bg-darken-2 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>D-2</span>
          </div>
          <div class="text-center bg-primary bg-darken-1 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>D-1</span>
          </div>
          <div class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>A</span>
          </div>
          <div class="text-center bg-primary bg-lighten-1 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>L-1</span>
          </div>
          <div class="text-center bg-primary bg-lighten-2 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>L-2</span>
          </div>
          <div class="text-center bg-primary bg-lighten-3 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>L-3</span>
          </div>
          <div class="text-center bg-primary bg-lighten-4 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>L-4</span>
          </div>
        </div>
      </b-card>
      <!-- Default Colors -->
      <!-- Gradient color start -->
      <b-card title="Gradient Colors">
        <b-card-text class="mb-0">
          We have make gradient color based on our default color:
        </b-card-text>

        <div class="d-flex justify-content-start flex-wrap">
          <div class="text-center colors-container bg-gradient-primary rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>Primary<br>gradient</span>
          </div>
          <div class="text-center colors-container bg-gradient-secondary rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>Secondary<br>gradient</span>
          </div>
          <div class="text-center colors-container bg-gradient-success rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>Success<br>gradient</span>
          </div>
          <div class="text-center colors-container bg-gradient-warning rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>Alert<br>gradient</span>
          </div>
          <div class="text-center colors-container bg-gradient-danger rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow">
            <span>Error<br>gradient</span>
          </div>
          <div class="text-center colors-container bg-gradient-info rounded text-white width-100 height-100 d-flex align-items-center justify-content-center my-1 ml-50 shadow px-1">
            <span>Info<br>gradient</span>
          </div>
        </div>
      </b-card>
      <!--/ Gradient color start -->
      <b-card title="Colors Documentation">
        <b-table-simple
          responsive
          bordered
          striped
        >
          <b-thead head-variant="danger">
            <b-tr>
              <b-th>Color Options</b-th>
              <b-th>Class</b-th>
              <b-th>Description</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr>
              <b-td><strong>Background Color</strong></b-td>
              <b-td><code>.bg-{color}</code></b-td>
              <b-td>This class is used for background color.</b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Lighten Background Color</strong></b-td>
              <b-td><code>.bg-{color}.bg-lighten-{value}</code></b-td>
              <b-td>
                <p class="mb-0">
                  For Lighten background color, these two classes are needed.</p>
                <p class="mb-0">
                  value: {1 | 2 | 3 | 4}</p>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Darken Background Color</strong></b-td>
              <b-td><code>.bg-{color}.bg-darken-{value}</code></b-td>
              <b-td>
                <p class="mb-0">
                  For Darken background color, these two classes are needed.</p>
                <p class="mb-0">
                  value: {1 | 2 | 3 | 4}</p>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Gradient Color</strong></b-td>
              <b-td><code>.bg-gradient-{color}</code></b-td>
              <b-td>This class is used for background with gradient color</b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Background with rgba Color</strong></b-td>
              <b-td><code>.bg-light-{color}</code></b-td>
              <b-td>This class is used to set background color with rgba format where alpha of given color is 0.12.</b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Background with bg-opacity Color</strong></b-td>
              <b-td><code>.overlay-{color}</code></b-td>
              <b-td>This class is used to set background color with opacity value of 0.6.</b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Border Color</strong></b-td>
              <b-td><code>.border-{color}</code></b-td>
              <b-td>
                <p class="mb-0">
                  This class is used for border color. You may use <code>.border-{direction}-{color}</code> class for any particular border color with direction.
                </p>
                <p class="mb-0">
                  direction: {top | right | bottom | left}
                </p>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Lighten Border Color</strong></b-td>
              <b-td><code>.border-{color}.border-lighten-{value}</code></b-td>
              <b-td>
                <p class="mb-0">
                  These classes are used for light border color. You may also use <code>.border-{direction}-{color}.border-{direction}-lighten-{value}</code> for any particular light border color with direction.
                </p>
                <p class="mb-0">
                  direction: {top | right | bottom | left}
                </p>
                <p class="mb-0">
                  value: {1 | 2 | 3 | 4}
                </p>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Lighten Border Color</strong></b-td>
              <b-td><code>.border-{color}.border-lighten-{value}</code></b-td>
              <b-td>
                <p class="mb-0">
                  These classes are used for light border color. You may also use <code>.border-{direction}-{color}.border-{direction}-lighten-{value}</code> for any particular light border color with direction.
                </p>
                <p class="mb-0">
                  direction: {top | right | bottom | left}
                </p>
                <p class="mb-0">
                  value: {1 | 2 | 3 | 4}
                </p>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Darken Border Color</strong></b-td>
              <b-td><code>.border-{color}.border-darken-{value}</code></b-td>
              <b-td>
                <p class="mb-0">
                  These classes are used for dark border color. You may also use <code>.border-{direction}-{color}.border-{direction}-darken-{value}</code> for any particular dark border color with direction.
                </p>
                <p class="mb-0">
                  direction: {top | right | bottom | left}
                </p>
                <p class="mb-0">
                  value: {1 | 2 | 3 | 4}
                </p>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td><strong>Text Color</strong></b-td>
              <b-td><code>.text-{color}</code></b-td>
              <b-td>This class is used for text color.</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig'
import { computed } from '@vue/composition-api'
import {
  BRow, BCol, BCard, BCardText, BTableSimple, BThead, BTr, BTh, BTbody, BTd,
} from 'bootstrap-vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTableSimple,
    BCardText,
    BThead,
    BTr,
    BTh,
    BTbody,
    BTd,
  },

  setup() {
    const { skin } = useAppConfig()
    const isDark = computed(() => skin.value === 'dark')
    return { skin, isDark }
  },
}
</script>

<style lang="scss">
.colors-list {
  li {
    padding: 0.4rem;
    svg {
      margin-right: 0.25rem;
    }
  }
}
.bg-darken {
  background-color: #161d32 !important;
}
.light-input-placeholder {
  color: #b9b9c3;
}
.dark-input-placeholder {
  color: #676d7d;
}
.light-input-placeholder-disabled {
  background-color: #f2f4f8;
  color: #b9b9c3;
}
.dark-input-placeholder-disabled {
  background-color: #283046;
  opacity: 0.5;
}
</style>
